// List picker — small modal that asks the user which shopping list to
// add recipe ingredients to. Remembers the last-picked list across
// invocations via localStorage so the next add is a one-tap default.
//
// Used by RecipeDetail's "Add ingredients" button. Could be reused
// later by anything else that targets a shopping list.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type ShoppingList } from "../../lib/rpc";

const LAST_LIST_KEY = "hub.recipes.addToList.lastId";

interface ListPickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (listId: number, listName: string) => void;
  notify: (msg: string) => void;
  // Optional badge to show on the confirmation button — used to surface
  // the active recipe scale (e.g. "Add ×2") so the user notices what's
  // about to land. Falls back to plain "Add" when 1x / unset.
  scale?: number;
  // Optional preferred default. When set, this list id takes priority
  // over the last-used localStorage value on open. Use when the caller
  // has a strong contextual default — e.g. Plan with AI was opened
  // FROM a specific list and should pre-select that list rather than
  // whatever the user last added to from a different surface.
  defaultListId?: number | null;
}

export function ListPicker({ open, onClose, onPick, notify, scale = 1, defaultListId = null }: ListPickerProps) {
  const [lists, setLists] = useState<ShoppingList[] | null>(null);
  const [picked, setPicked] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  // Load lists + restore last-picked when opening. We refetch every
  // open rather than caching across opens — lists are cheap and the
  // user may have added one in another tab/device.
  useEffect(() => {
    if (!open) return;
    setCreating(false);
    setNewName("");
    let cancelled = false;
    api.shoppingListLists().then((all) => {
      if (cancelled) return;
      const active = all.filter((l) => !l.archived);
      setLists(active);
      // Default-picked precedence:
      //   1. defaultListId from caller (e.g. Plan with AI's originating list)
      //   2. last-used from localStorage
      //   3. first available list
      // Skip "nothing picked" — that wastes a tap when the user
      // almost always uses the same list.
      let restored: number | null = null;
      if (defaultListId != null && active.some((l) => l.id === defaultListId)) {
        restored = defaultListId;
      } else {
        try {
          const saved = localStorage.getItem(LAST_LIST_KEY);
          const id = saved ? Number(saved) : NaN;
          if (Number.isFinite(id) && active.some((l) => l.id === id)) restored = id;
        } catch { /* ignore */ }
      }
      setPicked(restored ?? (active.length > 0 ? active[0].id : null));
    }).catch((e: any) => {
      notify(e?.message || "couldn't load lists");
      setLists([]);
    });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const confirm = () => {
    if (picked == null) return;
    const list = lists?.find((l) => l.id === picked);
    if (!list) return;
    try { localStorage.setItem(LAST_LIST_KEY, String(picked)); } catch { /* ignore */ }
    onPick(picked, list.name);
  };

  const createAndPick = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const fresh = await api.shoppingCreateList(name);
      try { localStorage.setItem(LAST_LIST_KEY, String(fresh.id)); } catch { /* ignore */ }
      onPick(fresh.id, fresh.name);
    } catch (e: any) {
      notify(e?.message || "couldn't create list");
    }
  };

  return (
    <div className="list-picker-bg" onClick={onClose}>
      <div className="list-picker" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Pick a list">
        <header className="list-picker-head">
          <span className="list-picker-title">Add to which list?</span>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={14} />
          </button>
        </header>

        {lists === null && (
          <p className="list-picker-loading">Loading lists…</p>
        )}

        {lists !== null && lists.length === 0 && !creating && (
          <>
            <p className="list-picker-empty">No active shopping lists yet.</p>
            <button className="btn btn-primary" onClick={() => setCreating(true)} type="button">
              <Icon name="plus" size={13} />
              <span>Create your first list</span>
            </button>
          </>
        )}

        {lists !== null && lists.length > 0 && !creating && (
          <div className="list-picker-rows">
            {lists.map((l) => (
              <button
                key={l.id}
                type="button"
                className={`list-picker-row${picked === l.id ? " active" : ""}`}
                onClick={() => setPicked(l.id)}
              >
                <span className="list-picker-row-name">{l.name}</span>
                {l.isShared && <span className="list-picker-row-badge">shared</span>}
                {picked === l.id && <Icon name="check" size={14} />}
              </button>
            ))}
            <button
              type="button"
              className="list-picker-create"
              onClick={() => setCreating(true)}
            >
              <Icon name="plus" size={13} />
              <span>New list…</span>
            </button>
          </div>
        )}

        {creating && (
          <div className="list-picker-new">
            <label htmlFor="list-picker-new-name">List name</label>
            <input
              id="list-picker-new-name"
              type="text"
              className="list-picker-input"
              value={newName}
              placeholder="e.g. Weekly groceries"
              onInput={(e) => setNewName((e.currentTarget as HTMLInputElement).value)}
              onKeyDown={(e) => { if (e.key === "Enter") createAndPick(); }}
              autofocus
            />
            <div className="list-picker-new-actions">
              <button className="btn-ghost" type="button" onClick={() => { setCreating(false); setNewName(""); }}>
                Cancel
              </button>
              <button className="btn btn-primary" type="button" onClick={createAndPick} disabled={!newName.trim()}>
                Create and add
              </button>
            </div>
          </div>
        )}

        {lists !== null && lists.length > 0 && !creating && (
          <div className="list-picker-actions">
            <button className="btn-ghost" onClick={onClose} type="button">Cancel</button>
            <button
              className="btn btn-primary"
              onClick={confirm}
              disabled={picked == null}
              type="button"
            >
              <Icon name="plus" size={13} />
              <span>Add{scale !== 1 ? ` ×${scale}` : ""}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
